import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, KeyRound, Trash2, UserCog } from "lucide-react";

const ROLES = [
  { v: "OWNER", l: "Собственик" }, { v: "WAITRESS", l: "Сервитьор" },
  { v: "KITCHEN", l: "Кухня" }, { v: "RECEPTION", l: "Рецепция" },
];
const roleLabel = (v) => ROLES.find((r) => r.v === v)?.l || v;

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [pwDlg, setPwDlg] = useState(null);
  const [pw, setPw] = useState("");

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!dlg.username || !dlg.password) return toast.error("Потребителско име и парола са задължителни");
    try { await api.post("/users", dlg); toast.success("Създаден"); setDlg(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const toggleActive = async (u) => { await api.put(`/users/${u.id}`, { active: !u.active }); load(); };
  const changeRole = async (u, role) => { await api.put(`/users/${u.id}`, { role }); toast.success("Ролята е сменена"); load(); };
  const savePw = async () => { if (!pw) return; await api.post(`/users/${pwDlg.id}/password`, { password: pw }); toast.success("Паролата е сменена"); setPwDlg(null); setPw(""); };
  const del = async (u) => { if (!window.confirm(`Изтриване на ${u.username}?`)) return; try { await api.delete(`/users/${u.id}`); load(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between">
        <div><h1 className="font-display text-4xl text-forest-deep">Потребители</h1><div className="gold-rule w-24 my-3" /></div>
        <Button onClick={() => setDlg({ username: "", password: "", role: "WAITRESS", full_name: "" })} className="rounded-xl gap-2 bg-forest text-white" data-testid="add-user-btn"><Plus className="w-4 h-4" /> Нов акаунт</Button>
      </div>

      <div className="bg-white rounded-2xl border border-gold/10 shadow-soft overflow-hidden mt-4">
        <table className="w-full text-sm">
          <thead className="bg-cream-muted text-forest text-left">
            <tr><th className="p-3">Потребител</th><th className="p-3">Име</th><th className="p-3">Роля</th><th className="p-3">Активен</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-gold/5" data-testid={`user-row-${u.username}`}>
                <td className="p-3 font-medium text-forest-deep flex items-center gap-2"><UserCog className="w-4 h-4 text-gold-dark" /> {u.username}</td>
                <td className="p-3">{u.full_name || "—"}</td>
                <td className="p-3">
                  <Select value={u.role} onValueChange={(v) => changeRole(u, v)}>
                    <SelectTrigger className="h-8 w-36 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="p-3"><Switch checked={u.active} onCheckedChange={() => toggleActive(u)} data-testid={`toggle-active-${u.username}`} /></td>
                <td className="p-3 text-right whitespace-nowrap">
                  <button onClick={() => setPwDlg(u)} className="text-gold-dark p-1" title="Смяна на парола" data-testid={`change-pw-${u.username}`}><KeyRound className="w-4 h-4" /></button>
                  {u.username !== "owner" && <button onClick={() => del(u)} className="text-destructive p-1"><Trash2 className="w-4 h-4" /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Нов акаунт</DialogTitle></DialogHeader>
          {dlg && (
            <div className="space-y-3">
              <div><Label className="text-xs">Потребителско име</Label><Input value={dlg.username} autoCapitalize="none" onChange={(e) => setDlg({ ...dlg, username: e.target.value })} data-testid="new-username" /></div>
              <div><Label className="text-xs">Име (по избор)</Label><Input value={dlg.full_name} onChange={(e) => setDlg({ ...dlg, full_name: e.target.value })} /></div>
              <div><Label className="text-xs">Парола</Label><Input type="text" value={dlg.password} onChange={(e) => setDlg({ ...dlg, password: e.target.value })} data-testid="new-password" /></div>
              <div><Label className="text-xs">Роля</Label>
                <Select value={dlg.role} onValueChange={(v) => setDlg({ ...dlg, role: v })}>
                  <SelectTrigger data-testid="new-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={create} className="bg-forest text-white rounded-xl" data-testid="save-user">Създай</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwDlg} onOpenChange={(o) => !o && setPwDlg(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Смяна на парола · {pwDlg?.username}</DialogTitle></DialogHeader>
          <Input type="text" placeholder="Нова парола" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-pw-input" />
          <DialogFooter><Button onClick={savePw} className="bg-forest text-white rounded-xl">Запази</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
